<template>
  <div :class="{ 'flex-row--noSaving': isSingleIssuesOffer }">
    <client-only>
      <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="false"></loading>
    </client-only>
    <div class="flex-row first red-text" :class="{ 'first--capped': hasNoSaving }" role="cell">
      <div class="flex-row__name">
        <span class="subscription__name">{{ getOfferSubName(offer.subName) }}</span>
        <span v-if="offer.subRenewalInfo" ref="subscriptionRenewal" class="subscription__renewal" @click.stop="toggleSubscriptionRenewalDDInfo">
          <span>{{ offer.subRenewalInfo }} <font-awesome-icon ref="infoIcon" :icon="['fas', 'info-circle']" class="fa fa-w-24 subscription__renewal-icon"/></span>
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-if="subscriptionRenewalDDInfoVisible"
            class="subscription__renewal-tooltip"
            role="tooltip"
            :style="{ '--arrow-left': arrowOffset }"
            v-html="subRenewalTooltipInfo"
          ></span>
          <!-- eslint-enable vue/no-v-html -->
        </span>
        <span v-if="offer.subType" class="subscription__type">{{ offer.subType }}</span>
      </div>

      <a v-if="offer.freeGiftEnabled" class="freegift-overlay" :alt="offer.freeGiftDescription" :title="offer.freeGiftDescription">
        <span class="freegift-overlay__txt1">FREE</span>
        <span class="freegift-overlay__txt2">GIFT</span>
      </a>
    </div>
    <div class="flex-row small-row" role="cell">{{ offer.numOfIssues }}</div>
    <div v-if="!hasNoSaving && offer.save" class="flex-row filler" role="cell">
      <span class="saving__first-line">{{ savingFirstLine }}</span>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <br /><span class="saving__second-line" v-html="savingSecondLine"></span>
    </div>
    <div v-else class="flex-row save filler" :class="{ 'save-filler--collapsed': hasNoSaving }" role="cell"></div>
    <div class="flex-row--payment" role="cell">
      <PaymentImages :offer-payment-type="offer.paymentType" :is-giftcard="offer.sku === 'gift-card' || offer.sku === 'e-giftcards'" />
    </div>
    <div ref="priceCell" class="flex-row red-text pdp-price last" :class="{ 'pdp-price--wrapped': shouldWrapPriceRow, 'last--no-saving': hasNoSaving }" role="cell">
      <div class="flex-row__price">{{ formatCurrency(offer.price) }}</div>
      <div v-if="isSingleIssuesOffer && offer.available === false && offer.qty === 0" class="add-to-basket-pdp add-to-basket-pdp--disabled" title="Out of Stock">
        OUT OF STOCK
      </div>
      <div v-else-if="magazine.enabled" class="add-to-basket-pdp" title="add to basket" @click.prevent="handleClickAction(offer, 'add')">BUY</div>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '~/lib/util/basket'
import basketMixin from '~/mixins/basket.vue'
import gtmProductPageMixin from '@/mixins/gtm/product-page'

export default {
  name: 'DesktopOffer',
  components: {
    PaymentImages: () => import('~/components/common/product/PaymentImages.vue')
  },
  mixins: [gtmProductPageMixin, basketMixin],
  props: {
    offer: {
      type: Object,
      required: true,
      default() {
        return {}
      }
    },
    magazine: {
      type: Object,
      required: true,
      default() {
        return {}
      }
    },
    styledClass: {
      type: String,
      required: false,
      default() {
        return 'pdp'
      }
    },
    isSingleIssuesOffer: {
      type: Boolean,
      required: true,
      default() {
        return false
      }
    },
    forceWrapAll: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    offerIndex: {
      type: Number,
      required: false,
      default() {
        return -1
      }
    },
    hasNoSaving: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    }
  },
  data() {
    return {
      isLoading: false,
      isPriceRowWrapped: false,
      lastEmittedNaturalWrap: null,
      savingFirstLine: this.offer.save ? `Save ${formatCurrency(this.offer.rrp - this.offer.price)} (${this.offer.save}%)` : '',
      savingSecondLine: this.offer.rrp ? `RRP <s>${formatCurrency(this.offer.rrp)}</s>` : '',
      subscriptionRenewalDDInfoVisible: false,
      subRenewalTooltipInfo: `You can choose to cancel your subscription at any time, including during a trial if one applies, and no further payments will be taken.<br/><br/>Your first Direct Debit payment for the initial price will be collected by ${this.magazine.publisher} shortly after placing your order. Your subscription will then automatically renew by Direct Debit at the frequency shown.`,
      arrowOffset: '0px'
    }
  },
  computed: {
    shouldWrapPriceRow() {
      return this.forceWrapAll
    }
  },
  created() {
    this.$root.$on('desktop-offer-renewal-tooltip-opened', this.handleOtherTooltipOpened)
    this.resizeDebounceTimer = null
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
    this.$nextTick(() => {
      this.calculateArrowPosition()
      this.updatePriceRowWrapState()
    })
    window.addEventListener('resize', this.calculateArrowPosition)
    window.addEventListener('resize', this.debouncedUpdatePriceRowWrapState)
  },
  updated() {
    this.calculateArrowPosition()
  },
  beforeDestroy() {
    this.$root.$off('desktop-offer-renewal-tooltip-opened', this.handleOtherTooltipOpened)
    document.removeEventListener('click', this.handleDocumentClick)
    window.removeEventListener('resize', this.calculateArrowPosition)
    window.removeEventListener('resize', this.debouncedUpdatePriceRowWrapState)
    clearTimeout(this.resizeDebounceTimer)
  },
  methods: {
    debouncedUpdatePriceRowWrapState() {
      clearTimeout(this.resizeDebounceTimer)
      this.resizeDebounceTimer = setTimeout(() => {
        this.updatePriceRowWrapState()
      }, 100)
    },
    updatePriceRowWrapState() {
      const priceCell = this.$refs.priceCell

      if (!priceCell) {
        if (this.isPriceRowWrapped !== false) {
          this.isPriceRowWrapped = false
        }
        this.emitNaturalWrapChange(false)
        return
      }

      const priceElement = priceCell.querySelector('.flex-row__price')
      const ctaElement = priceCell.querySelector('.add-to-basket-pdp')

      if (!priceElement || !ctaElement) {
        if (this.isPriceRowWrapped !== false) {
          this.isPriceRowWrapped = false
        }
        this.emitNaturalWrapChange(false)
        return
      }

      const previousCellFlexWrap = priceCell.style.flexWrap
      const previousPriceFlexShrink = priceElement.style.flexShrink
      const previousPriceFlexBasis = priceElement.style.flexBasis
      const previousCtaFlexShrink = ctaElement.style.flexShrink
      const previousCtaFlexBasis = ctaElement.style.flexBasis

      priceCell.style.flexWrap = 'nowrap'
      priceElement.style.flexShrink = '0'
      priceElement.style.flexBasis = 'auto'
      ctaElement.style.flexShrink = '0'
      ctaElement.style.flexBasis = 'auto'

      const isWrapped = priceCell.scrollWidth > priceCell.clientWidth

      priceCell.style.flexWrap = previousCellFlexWrap
      priceElement.style.flexShrink = previousPriceFlexShrink
      priceElement.style.flexBasis = previousPriceFlexBasis
      ctaElement.style.flexShrink = previousCtaFlexShrink
      ctaElement.style.flexBasis = previousCtaFlexBasis

      if (this.isPriceRowWrapped !== isWrapped) {
        this.isPriceRowWrapped = isWrapped
      }

      this.emitNaturalWrapChange(isWrapped)
    },
    emitNaturalWrapChange(isWrapped) {
      if (this.lastEmittedNaturalWrap === isWrapped) {
        return
      }

      this.lastEmittedNaturalWrap = isWrapped
      this.$emit('natural-wrap-change', { index: this.offerIndex, isWrapped })
    },
    calculateArrowPosition() {
      try {
        if (!this.$refs.subscriptionRenewal) return

        const renewal = this.$refs.subscriptionRenewal
        const icon = renewal.querySelector('.subscription__renewal-icon')

        if (!icon) return

        const iconRect = icon.getBoundingClientRect()
        const renewalRect = renewal.getBoundingClientRect()
        const offset = iconRect.left - renewalRect.left + iconRect.width / 2
        this.arrowOffset = `calc(${offset}px - 5px)`
      } catch (error) {
        console.error('Error calculating arrow position:', error)
      }
    },
    toggleSubscriptionRenewalDDInfo() {
      const willOpen = !this.subscriptionRenewalDDInfoVisible
      this.subscriptionRenewalDDInfoVisible = willOpen

      if (willOpen) {
        this.$root.$emit('desktop-offer-renewal-tooltip-opened', this._uid)
      }
    },
    handleOtherTooltipOpened(openedByUid) {
      if (openedByUid !== this._uid) {
        this.subscriptionRenewalDDInfoVisible = false
      }
    },
    handleDocumentClick(event) {
      const renewalElement = this.$refs.subscriptionRenewal

      if (!renewalElement || renewalElement.contains(event.target)) {
        return
      }

      this.subscriptionRenewalDDInfoVisible = false
    },
    getOfferSubName(value) {
      if (!value) {
        return ''
      }

      return value.replace(/\s*\((Print & Digital|Digital Edition|Print Edition|Membership)\)\s*$/i, '').trim()
    },
    formatCurrency(value) {
      return formatCurrency(value)
    },
    async handleClickAction(offer, action) {
      console.log('DesktopOffer handleClickAction offer=', offer)
      console.log('DesktopOffer handleClickAction action=', action)

      // OUT OF STOCK for SINGLE_ISSUES
      if (offer.type === 'SINGLE_ISSUES' && offer.qty === 0 && offer.avaiable === false) {
        return
      }

      const item = {
        title: this.magazine.title,
        imageUrl: offer.imageUrl,
        productId: this.magazine.productId,
        id: this.magazine.id,
        sku: this.magazine.sku,
        offer,
        category: this.magazine.category,
        subCategory: this.magazine.subCategory
      }

      // Update the BE Basket data
      const ret = await this.updateBasket({ item, action, source: 'PRODUCT', sourceVersion: this.$config.ProductPageVersion })

      // GTM & FB push events integration
      !ret.error && this.pushGTMAndFBAfterButtonClick({ action, item })

      // Show the basket overlay
      !ret.error && this.$nuxt.$emit('SHOW_BASKET', true)
    }
  }
}
</script>

<style lang="scss">
.add-to-basket-pdp--disabled {
  font-size: 14px !important;
  background-color: gray !important;
}
.table-container {
  display: block;
  //   margin: 2em auto;
  width: 100%;
  &--old {
    font-size: 12px;
    .flex-row {
      font-size: 12px;
      padding: 10px 0 10px 8px;
      &__price {
        margin-right: 10px;
        min-width: 50px;
      }
      &__payment {
        width: 15%;
        text-align: center;
        align-self: center;
      }
    }
    .offer-payment-type {
      &--cc {
        // max-width: 70px;
      }
      &--dd {
        // padding: 0;
        // max-width: 70px;
      }
    }

    .pdp-price {
      color: #ff0d00;
      font-weight: 700;
      font-size: 16px;
    }
    .subscription {
      &__name {
        font-size: 12px;
        font-weight: 700;
        color: #ff0d00;
      }
      &__renewal {
        position: relative;
        display: inline-block;
        font-size: 11px;
        font-weight: 400;
        color: #353535;
        cursor: pointer;
      }
      &__renewal-tooltip {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 10;
        min-width: 300px;
        padding: 15px;
        border-radius: 4px;
        font-size: 11px;
        line-height: 1.3;
        color: #fff;
        background-color: #666666;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        white-space: normal;
      }
      &__renewal-tooltip::before {
        content: '';
        position: absolute;
        top: -5px;
        left: var(--arrow-left, 0);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-bottom: 5px solid #666666;
      }
      &__renewal-icon {
        background-color: #fff;
        color: #a1a1a1;
        border-radius: 50%;
      }
      &__type {
        font-size: 11px;
        font-weight: 600;
        color: #353535;
      }
    }
    .saving {
      &__first-line {
        font-size: 12px;
        color: #353535;
        font-weight: 700;
      }
      &__second-line {
        font-weight: 400;
        font-size: 12px;
        color: #666666;
      }
    }
    .first {
      font-size: 12px;
      text-align: left;
      color: #ff0d00;
      font-weight: 700;
      display: flex;
      flex-wrap: nowrap;
      justify-content: space-between;
      align-items: center;

      &--capped {
        max-width: 220px;
      }
    }
    .last {
      width: calc(100% / 4);
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;

      &--no-saving {
        flex: 1 1 auto;
        justify-content: center;
        align-items: center;
        column-gap: 50px;
        text-align: center;

        .flex-row__price {
          margin-right: 0;
        }
      }

      &.pdp-price--wrapped {
        flex-wrap: wrap;
        justify-content: center;
        column-gap: 0;

        .flex-row__price,
        .add-to-basket-pdp,
        .add-to-basket-pdp--disabled {
          flex-basis: 100%;
          text-align: center;
        }
      }
    }
    .save-filler--collapsed {
      display: none;
    }
  }
  .header-offers {
    &--old {
      padding: 2px 5px;
      border-left: 1px solid #fff;
      font-size: 12px;
      font-weight: 700;
      font-family: 'Open Sans', sans-serif;
      color: #fff;
      background: #666;
      .flex-row {
        padding: 0;
        text-align: center;
      }
      &:hover {
        background: #666;
      }
      .first {
        display: block;
        font-size: 12px;
        color: #fff;
      }
      .last {
        display: block;
        font-size: 12px;
        color: #fff;
      }
    }
    &--noSaving {
      .first {
        width: calc(100% / 3);
        max-width: 220px;
      }
      .small-row {
        width: calc(100% / 6);
      }
      .last {
        width: calc(100% / 3);
      }
      .filler {
        display: none;
      }
    }
  }
  .flex-row--noSaving {
    .first {
      width: calc(100% / 3);
    }
    .small-row {
      width: calc(100% / 6);
    }
    .last {
      width: calc(100% / 3);
    }
    .filler {
      display: none;
    }
  }
  .flex-row__name {
    display: flex;
    flex-flow: column;
  }
}
@media (max-width: $screen-xs-max) {
  .desktop {
    display: none;
  }
}

.desktop {
  .freegift-overlay {
    &:hover {
      cursor: default;
    }
    display: block;
    position: relative;
    min-width: 44px;
    max-width: 44px;
    min-height: 44px;
    max-height: 44px;
    z-index: 0;
    background-color: #df0000;
    border-radius: 30px;
    border: 2px #fff solid;
    text-align: center;
    line-height: 13px;
    left: 5px;
  }
  .freegift-overlay__txt1 {
    position: absolute;
    color: #fff;
    font-weight: 700;
    width: 44px;
    right: -2px;
    top: 8px;
    font-size: 13px;
    text-align: center;
  }

  .freegift-overlay__txt2 {
    position: absolute;
    color: #fff;
    font-weight: 700;
    width: 44px;
    right: -2px;
    top: 19px;
    font-size: 13px;
    text-align: center;
  }
}
</style>
